/**
 * Goals composable
 * Provides methods for managing Goals in Firestore
 */
import { ref, computed, onUnmounted } from 'vue'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore'
import { getFirebaseDb } from '@/core/firebase/firebase'
import { useCloudFunctions } from './useCloudFunctions'

// Max number of agents whose sales are fetched from the ERP at once. Each fetch
// is an independent Cloud Function round-trip; a small cap parallelizes them
// without overwhelming the browser connection pool or the ERP.
const AGENT_CONCURRENCY = 5

/**
 * Run `fn` over `items` with at most `limit` in flight at a time, preserving
 * result order. Used to fan agent sales fetches out concurrently instead of
 * awaiting them one-by-one.
 * @param {Array} items
 * @param {number} limit
 * @param {(item: any, index: number) => Promise<any>} fn
 * @returns {Promise<Array>}
 */
async function mapWithConcurrency(items, limit, fn) {
  const results = new Array(items.length)
  let next = 0
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++
      results[index] = await fn(items[index], index)
    }
  })
  await Promise.all(runners)
  return results
}

// Goal types
export const GOAL_TYPES = {
  QUANTITY: 'כמותי',
  DISTRIBUTION: 'פיזור',
  FINANCIAL: 'כספי',
}

// Makat association types
export const MAKAT_TYPES = {
  INDIVIDUAL: 'מקט',
  CENTER: 'מרכז',
}

/**
 * Create a new goal object with default values
 * @returns {Object} New goal object
 */
export function createNewGoal() {
  return {
    uid: '',
    title: '',
    type: '',
    makat_type: '',
    active: true,
    includedSahar: true,
    products: [],
    agents: [],
    date_start: '',
    date_end: '',
    date_start_js: null,
    date_end_js: null,
    created: null,
    edited: null,
    fetched: null,
  }
}

/**
 * Composable for managing Goals
 * @returns {Object} Goals methods and state
 */
export function useGoals() {
  const db = getFirebaseDb()
  const { getAgentSales } = useCloudFunctions()

  // State
  const loading = ref(false)
  const error = ref(null)
  const currentGoals = ref([])
  const pastGoals = ref([])
  const currentGoalsReady = ref(false)
  const pastGoalsReady = ref(false)

  // Unsubscribe functions for listeners
  let currentGoalsUnsubscribe = null
  let pastGoalsUnsubscribe = null

  // ============================================
  // Goals CRUD Operations
  // ============================================

  /**
   * Create a new goal
   * @param {Object} goalData - Goal data
   * @returns {Promise<string>} Goal document ID
   */
  async function createGoal(goalData) {
    loading.value = true
    error.value = null

    try {
      const goalsRef = collection(db, 'Goals')
      const newDocRef = doc(goalsRef)
      const goalId = newDocRef.id

      // Convert dates to Firestore Timestamps
      const goal = {
        ...goalData,
        uid: goalId,
        date_start_js: Timestamp.fromDate(new Date(goalData.date_start)),
        date_end_js: Timestamp.fromDate(new Date(goalData.date_end)),
        created: Timestamp.now(),
      }

      await setDoc(newDocRef, goal)
      return goalId
    } catch (err) {
      console.error('Error creating goal:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Update an existing goal
   * @param {string} goalId - Goal document ID
   * @param {Object} goalData - Updated goal data
   */
  async function updateGoal(goalId, goalData) {
    loading.value = true
    error.value = null

    try {
      const goalRef = doc(db, 'Goals', goalId)

      // Convert dates if changed
      const updates = {
        ...goalData,
        edited: Timestamp.now(),
      }

      if (goalData.date_start) {
        updates.date_start_js = Timestamp.fromDate(new Date(goalData.date_start))
      }
      if (goalData.date_end) {
        updates.date_end_js = Timestamp.fromDate(new Date(goalData.date_end))
      }

      await setDoc(goalRef, updates)
    } catch (err) {
      console.error('Error updating goal:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Delete a goal
   * @param {string} goalId - Goal document ID
   */
  async function deleteGoal(goalId) {
    loading.value = true
    error.value = null

    try {
      const goalRef = doc(db, 'Goals', goalId)
      await deleteDoc(goalRef)
    } catch (err) {
      console.error('Error deleting goal:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get a single goal by ID
   * @param {string} goalId - Goal document ID
   * @returns {Promise<Object|null>} Goal data or null
   */
  async function getGoal(goalId) {
    try {
      const goalRef = doc(db, 'Goals', goalId)
      const snapshot = await getDoc(goalRef)
      return snapshot.exists() ? snapshot.data() : null
    } catch (err) {
      console.error('Error fetching goal:', err)
      throw err
    }
  }

  // ============================================
  // Real-time Listeners
  // ============================================

  /**
   * Subscribe to current (active) goals
   * Goals where date_end_js > now
   */
  function subscribeToCurrentGoals() {
    currentGoalsReady.value = false
    const now = Timestamp.now()
    const goalsRef = collection(db, 'Goals')
    const q = query(goalsRef, where('date_end_js', '>', now))

    currentGoalsUnsubscribe = onSnapshot(
      q,
      (snapshot) => {
        currentGoalsReady.value = true
        snapshot.docChanges().forEach((change) => {
          const goalData = change.doc.data()

          if (change.type === 'added') {
            // Sort agents by id when adding
            if (goalData.agents) {
              goalData.agents.sort((a, b) => a.id - b.id)
            }
            currentGoals.value.push(goalData)
          }

          if (change.type === 'modified') {
            const index = currentGoals.value.findIndex((g) => g.uid === change.doc.id)
            if (index !== -1) {
              currentGoals.value[index] = goalData
            }
          }

          if (change.type === 'removed') {
            const index = currentGoals.value.findIndex((g) => g.uid === change.doc.id)
            if (index !== -1) {
              currentGoals.value.splice(index, 1)
            }
          }
        })
      },
      (err) => {
        console.error('Current goals listener error:', err)
        error.value = err.message
      }
    )
  }

  /**
   * Subscribe to past (expired) goals
   * Goals where date_end_js <= now
   */
  function subscribeToPastGoals() {
    pastGoalsReady.value = false
    const now = Timestamp.now()
    const goalsRef = collection(db, 'Goals')
    const q = query(goalsRef, where('date_end_js', '<=', now))

    pastGoalsUnsubscribe = onSnapshot(
      q,
      (snapshot) => {
        pastGoalsReady.value = true
        snapshot.docChanges().forEach((change) => {
          const goalData = change.doc.data()

          if (change.type === 'added') {
            if (goalData.agents) {
              goalData.agents.sort((a, b) => a.id - b.id)
            }
            pastGoals.value.push(goalData)
          }

          if (change.type === 'modified') {
            const index = pastGoals.value.findIndex((g) => g.uid === change.doc.id)
            if (index !== -1) {
              pastGoals.value[index] = goalData
            }
          }

          if (change.type === 'removed') {
            const index = pastGoals.value.findIndex((g) => g.uid === change.doc.id)
            if (index !== -1) {
              pastGoals.value.splice(index, 1)
            }
          }
        })
      },
      (err) => {
        console.error('Past goals listener error:', err)
        error.value = err.message
      }
    )
  }

  /**
   * Unsubscribe from all listeners
   */
  function unsubscribeAll() {
    if (currentGoalsUnsubscribe) {
      currentGoalsUnsubscribe()
      currentGoalsUnsubscribe = null
    }
    if (pastGoalsUnsubscribe) {
      pastGoalsUnsubscribe()
      pastGoalsUnsubscribe = null
    }
    currentGoals.value = []
    pastGoals.value = []
  }

  // ============================================
  // Products (Catalog) Operations
  // ============================================

  /**
   * Fetch product by makat
   * @param {string} makat - Product makat
   * @returns {Promise<Object|null>} Product data or null
   */
  async function getProductByMakat(makat) {
    try {
      const itemRef = doc(db, 'Catalog', makat)
      const snapshot = await getDoc(itemRef)

      if (!snapshot.exists()) {
        return null
      }

      const product = snapshot.data()
      return {
        name: product.name,
        makat: product.makat,
        boxSize: product.boxSize,
        image: product.image,
        db_image: product.db_image || null,
      }
    } catch (err) {
      console.error('Error fetching product:', err)
      throw err
    }
  }

  /**
   * Fetch products by paritMerakez (center makat)
   * @param {string} centerMakat - Center makat value
   * @returns {Promise<Array>} Array of products
   */
  async function getProductsByCenterMakat(centerMakat) {
    try {
      const itemsRef = collection(db, 'Catalog')
      const q = query(itemsRef, where('paritMerakez', '==', centerMakat))
      const snapshot = await getDocs(q)

      if (snapshot.empty) {
        return []
      }

      return snapshot.docs.map((doc) => {
        const product = doc.data()
        return {
          name: product.name,
          makat: product.makat,
          boxSize: product.boxSize,
          image: product.image,
          db_image: product.db_image || null,
        }
      })
    } catch (err) {
      console.error('Error fetching products by center makat:', err)
      throw err
    }
  }

  /**
   * Validate and fetch products based on makat type
   * @param {string} makatInput - Space-separated makats or single center makat
   * @param {string} makatType - 'מקט' for individual or 'מרכז' for center
   * @returns {Promise<{products: Array, error: string|null}>}
   */
  async function validateAndFetchProducts(makatInput, makatType) {
    if (!makatInput || !makatInput.trim()) {
      return { products: [], error: 'נא למלא מספרי מקטים' }
    }

    const products = []

    if (makatType === MAKAT_TYPES.INDIVIDUAL) {
      // Individual makats separated by spaces
      const makats = makatInput.trim().split(/\s+/)

      for (const makat of makats) {
        const product = await getProductByMakat(makat)
        if (!product) {
          return { products: [], error: `המקט ${makat} איננו נמצא במערכת` }
        }
        products.push(product)
      }
    } else if (makatType === MAKAT_TYPES.CENTER) {
      // Center makat
      const fetchedProducts = await getProductsByCenterMakat(makatInput.trim())
      if (fetchedProducts.length === 0) {
        return { products: [], error: `מקט מרכז ${makatInput} איננו נמצא במערכת` }
      }
      products.push(...fetchedProducts)
    }

    return { products, error: null }
  }

  // ============================================
  // Agents Operations
  // ============================================

  /**
   * Fetch all agents (users with agent number > 0)
   * @returns {Promise<Array>} Array of agent objects for goal
   */
  async function fetchAgents() {
    try {
      const usersRef = collection(db, 'Users')
      const q = query(usersRef, where('agent', '>', '0'))
      const snapshot = await getDocs(q)

      if (snapshot.empty) {
        return []
      }

      const agents = snapshot.docs
        .map((doc) => doc.data())
        .sort((a, b) => a.agent - b.agent)
        .map((user) => ({
          id: user.agent,
          uid: user.uid,
          name: user.name || '',
          goal: 0,
          amount: 0,
          image: user.photoURL || null,
          agents_clients: [],
          missing_clients: [],
        }))

      return agents
    } catch (err) {
      console.error('Error fetching agents:', err)
      throw err
    }
  }

  // ============================================
  // Goal Results Processing
  // ============================================

  /**
   * Update goal results by fetching sales data from Priority ERP
   * @param {Object} goal - Goal object to update
   * @returns {Promise<Object>} Updated goal with results
   */
  async function updateGoalResults(goal) {
    loading.value = true
    error.value = null

    try {
      // Build product names map for lookup
      const productNames = new Map()
      const goalMakats = goal.products.map((product) => {
        productNames.set(product.makat, product.name)
        return product.makat
      })

      // Process agents concurrently (capped) instead of one-by-one. Each agent's
      // ERP fetch is independent, so this collapses N serial round-trips into a
      // few concurrent waves — the core fix for long-relevance goals timing out.
      const processingResults = await mapWithConcurrency(
        goal.agents,
        AGENT_CONCURRENCY,
        async (agent) => {
          agent.amount = 0
          const agentsClients = new Map()

          try {
            // Fetch sales from Cloud Function
            const salesData = await getAgentSales({
              date_start: goal.date_start,
              date_end: goal.date_end,
              agent_number: agent.id,
              makats: goalMakats,
            })

            // Process sales based on goal type
            salesData.sales.forEach((sale) => {
              const client = sale.client
              const name = sale.name || 'לא צויין'
              const city = sale.city || 'לא צויין'
              const visitation = sale.visitation || null

              if (!agentsClients.has(client)) {
                agentsClients.set(client, {
                  name,
                  makat: client,
                  items: new Map(),
                  city,
                  visitation,
                })
              }

              const clientEntry = agentsClients.get(client)

              sale.items.forEach((item) => {
                if (productNames.has(item.makat)) {
                  // Determine amount based on goal type
                  const amountValue =
                    goal.type === GOAL_TYPES.FINANCIAL
                      ? item.total_cost || 0
                      : Number(item.amount) || 0

                  if (clientEntry.items.has(item.makat)) {
                    clientEntry.items.get(item.makat).amount += amountValue
                  } else {
                    // Product name is NOT stored per line — it's derived in the UI
                    // from goal.products, keeping the goal doc under Firestore's 1 MiB cap.
                    clientEntry.items.set(item.makat, {
                      ...item,
                      amount: amountValue,
                    })
                  }

                  agent.amount += amountValue
                }
              })
            })

            // remained_items (goal makats the client didn't buy) are NOT stored —
            // they're derived in the UI from goal.products. Storing them per client
            // for every makat pushed the goal doc past Firestore's 1 MiB limit.
            agentsClients.forEach((clientEntry) => {
              clientEntry.items = Array.from(clientEntry.items.values())
            })

            agent.agents_clients = Array.from(agentsClients.values())
            agent.missing_clients = salesData.missing_clients

            return { agent: agent.name, status: 'success' }
          } catch (err) {
            console.error(`Error processing agent ${agent.name}:`, err)
            agent.amount = 0
            agent.agents_clients = []
            agent.missing_clients = []
            return {
              agent: agent.name,
              status: 'error',
              error: err.message,
            }
          }
        }
      )

      // Update goal in Firestore if at least some agents succeeded
      const successCount = processingResults.filter((r) => r.status === 'success').length

      if (successCount > 0) {
        goal.fetched = Timestamp.now()
        const goalRef = doc(db, 'Goals', goal.uid)
        await setDoc(goalRef, goal)
      }

      return {
        goal,
        results: processingResults,
        successCount,
        errorCount: processingResults.filter((r) => r.status === 'error').length,
      }
    } catch (err) {
      console.error('Error updating goal results:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // Clean up listeners on unmount
  onUnmounted(() => {
    unsubscribeAll()
  })

  return {
    // State
    loading,
    error,
    currentGoals,
    pastGoals,
    currentGoalsReady,
    pastGoalsReady,

    // CRUD
    createGoal,
    updateGoal,
    deleteGoal,
    getGoal,

    // Listeners
    subscribeToCurrentGoals,
    subscribeToPastGoals,
    unsubscribeAll,

    // Products
    getProductByMakat,
    getProductsByCenterMakat,
    validateAndFetchProducts,

    // Agents
    fetchAgents,

    // Results
    updateGoalResults,

    // Utilities
    createNewGoal,
    GOAL_TYPES,
    MAKAT_TYPES,
  }
}
